import { useEffect, useState } from 'react'
import { hasRecentAuthentication, type PmmSession } from '../domain/auth'
import {
  consentScopeMeta,
  isConsentGranted,
  type ConsentScope,
  type ConsentState
} from '../domain/consent'
import type { DevicePrivacySettings, PrivacyJob } from '../domain/privacy'
import type { PmmRepository } from '../data/repository'
import { disablePmmPush } from '../notifications/pushClient'

interface PrivacyCenterProps {
  repository: PmmRepository
  session: PmmSession
  onBack(): void
  onQuickSignOut(): Promise<void>
  onReauthenticate(password: string): Promise<void>
}

const optionalScopes = Object.keys(consentScopeMeta).filter((scope) => consentScopeMeta[scope as ConsentScope].optional) as ConsentScope[]

export function PrivacyCenter({ repository, session, onBack, onQuickSignOut, onReauthenticate }: PrivacyCenterProps) {
  const [consent, setConsent] = useState<ConsentState>({ version: 0, scopes: {} })
  const [device, setDevice] = useState<DevicePrivacySettings>({ trustedDevice: false, privacyMode: true, persistentCacheActive: false })
  const [exportJob, setExportJob] = useState<PrivacyJob>()
  const [deletionJob, setDeletionJob] = useState<PrivacyJob>()
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [reauthOpen, setReauthOpen] = useState(false)
  const [password, setPassword] = useState('')

  useEffect(() => {
    void repository.getPrivacySnapshot().then((snapshot) => {
      setConsent(snapshot.consent)
      setDevice(snapshot.device)
      setExportJob(snapshot.exportJob)
      setDeletionJob(snapshot.deletionJob)
    }).catch(() => setError('Privacy settings could not be loaded. No choice was changed.'))
  }, [repository])

  async function decide(scope: ConsentScope, grant: boolean) {
    setError('')
    const current = consent.scopes[scope]
    try {
      if (scope === 'push_notifications' && !grant) await disablePmmPush(repository)
      const next = await repository.recordConsentDecision({
        scope,
        decision: grant ? 'grant' : current?.granted ? 'revoke' : 'deny',
        previousEventId: current?.lastEventId ?? null,
        uiSource: 'privacy_centre'
      })
      setConsent(next)
      setNotice(`${consentScopeMeta[scope].title} ${grant ? 'enabled' : 'disabled'}.`)
    } catch {
      setError('That consent state changed elsewhere. Reload the current choice before trying again.')
    }
  }

  async function updateDevice(patch: Partial<Pick<DevicePrivacySettings, 'trustedDevice' | 'privacyMode'>>) {
    try {
      const next = await repository.updateDevicePrivacy({
        trustedDevice: patch.trustedDevice ?? device.trustedDevice,
        privacyMode: patch.privacyMode ?? device.privacyMode
      })
      setDevice(next)
      setNotice(next.persistentCacheActive ? 'Device privacy preference updated. Encrypted offline Today remains active.' : 'Device privacy preference updated. This browser is in memory-only mode.')
    } catch {
      setError('The device preference was not changed.')
    }
  }

  async function request(kind: 'export' | 'deletion') {
    if (!hasRecentAuthentication(session)) {
      setReauthOpen(true)
      return
    }
    try {
      const job = kind === 'export' ? await repository.requestExport() : await repository.requestDeletion()
      if (kind === 'export') setExportJob(job)
      else setDeletionJob(job)
      setNotice(`${kind === 'export' ? 'Export' : 'Deletion'} request recorded.`)
    } catch {
      setError('The request was not recorded. Re-authenticate and try again.')
    }
  }

  async function reauthenticate() {
    try {
      await onReauthenticate(password)
      setReauthOpen(false)
      setPassword('')
      setNotice('Identity reconfirmed. Rights requests remain server-verified.')
    } catch {
      setError('Re-authentication was not accepted.')
    }
  }

  return (
    <main className="privacy-centre" aria-labelledby="privacy-title">
      <section className="privacy-hero">
        <button type="button" className="back-action" onClick={onBack}>Back to practice</button>
        <p className="eyebrow">Privacy centre</p>
        <h1 id="privacy-title">Your inner life is not product inventory.</h1>
        <p>See what is allowed, withdraw optional uses, protect this device and exercise your data rights without entering Coach workspace.</p>
      </section>

      {(notice || error) && <div className={error ? 'privacy-message is-error' : 'privacy-message'} role={error ? 'alert' : 'status'}>{error || notice}</div>}

      <section className="privacy-grid">
        <article className="privacy-panel consent-panel">
          <div className="panel-heading"><div><p className="eyebrow">Purpose by purpose</p><h2>Consent controls</h2></div><span className="private-badge">Append-only record</span></div>
          <div className="core-consent"><div><strong>{consentScopeMeta.core_service.title}</strong><p>{consentScopeMeta.core_service.summary}</p><small>{consentScopeMeta.core_service.collectionNotice}</small></div><span>Required while active</span></div>
          <div className="consent-list">
            {optionalScopes.map((scope) => {
              const meta = consentScopeMeta[scope]
              const granted = isConsentGranted(consent, scope)
              return <div className="consent-row" key={scope}><div><strong>{meta.title}</strong><p>{meta.summary}</p><details><summary>What is collected and why</summary><p>{meta.collectionNotice}</p><small>Data: {meta.dataCategories.join(', ')}. Processors: {meta.processorCategories.join(', ')}.</small></details></div><button type="button" className={granted ? 'consent-toggle is-on' : 'consent-toggle'} aria-pressed={granted} onClick={() => void decide(scope, !granted)}><span />{granted ? 'Allowed' : 'Off'}</button></div>
            })}
          </div>
        </article>

        <div className="privacy-side-stack">
          <article className="privacy-panel device-panel">
            <p className="eyebrow">This browser</p><h2>Shared-device protection</h2>
            <label className="device-choice"><span><strong>Private, trusted device</strong><small>Required before the separately consented, encrypted offline Today vault can run.</small></span><input type="checkbox" checked={device.trustedDevice} onChange={(event) => void updateDevice({ trustedDevice: event.target.checked })} /></label>
            <label className="device-choice"><span><strong>Privacy mode</strong><small>Keep intimate history out of incidental surfaces and previews.</small></span><input type="checkbox" checked={device.privacyMode} onChange={(event) => void updateDevice({ privacyMode: event.target.checked })} /></label>
            <div className="cache-state"><span />{device.persistentCacheActive ? 'Encrypted offline Today active' : 'Memory-only mode active'}</div>
            <button type="button" className="clear-device-action" onClick={() => void repository.clearSensitiveDeviceState().then(() => setNotice('App-managed device state cleared.'))}>Clear this device</button>
            <button type="button" className="signout-action" onClick={() => void onQuickSignOut()}>Quick sign out</button>
          </article>

          <article className="privacy-panel rights-panel">
            <p className="eyebrow">Access and control</p><h2>Your data rights</h2>
            <p>Requests require a recently confirmed identity and are processed as visible server-side jobs.</p>
            <button type="button" onClick={() => void request('export')}>Request data export</button>
            {exportJob && <small>Export {exportJob.state}  -  {exportJob.jobId}</small>}
            <button type="button" className="danger-outline" onClick={() => void request('deletion')}>Start account deletion</button>
            {deletionJob && <small>Deletion {deletionJob.state}  -  {deletionJob.jobId}</small>}
          </article>
        </div>
      </section>

      {reauthOpen && <div className="reauth-dialog" role="dialog" aria-modal="true" aria-labelledby="reauth-title"><div><p className="eyebrow">Recent authentication</p><h2 id="reauth-title">Confirm it is really you</h2><p>Export and deletion require a fresh identity check.</p>{repository.mode === 'firestore' && <label><span>Password</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>}<div><button type="button" className="secondary-action" onClick={() => setReauthOpen(false)}>Cancel</button><button type="button" onClick={() => void reauthenticate()}>Confirm identity</button></div></div></div>}
    </main>
  )
}
